// File: frontend/src/pages/attendance/MarkAttendance.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';
import PageHeader from '../../components/PageHeader';

const MarkAttendance = () => {
  const { school } = useSchool();
  const [selectedClass, setSelectedClass] = useState('1');
  const [selectedSection, setSelectedSection] = useState('A');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [students, setStudents] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [remarksMap, setRemarksMap] = useState({});

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  /**
   * Fetch student roster and current attendance status for selected class and date
   */
  const handleLoadStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await api.get(`/api/v1/attendance/class/${selectedClass}`, {
        params: {
          date: selectedDate,
          section: selectedSection
        }
      });

      if (response.data?.success) {
        const studentList = response.data.data?.students || [];
        setStudents(studentList);

        // Map initial statuses (default to 'present' if not previously marked)
        const initialStatus = {};
        const initialRemarks = {};
        studentList.forEach((st) => {
          initialStatus[st.student_id] = st.status || 'present';
          initialRemarks[st.student_id] = st.remarks || '';
        });
        setAttendanceMap(initialStatus);
        setRemarksMap(initialRemarks);
      } else {
        setError(response.data?.message || 'Failed to load students');
      }
    } catch (err) {
      console.error('Error loading class attendance:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching student roster');
    } finally {
      setLoading(false);
    }
  };

  // Auto load students when class, section, or date changes
  useEffect(() => {
    handleLoadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection, selectedDate]);

  /**
   * Update status for a specific student
   */
  const handleStatusChange = (studentId, status) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [studentId]: status
    }));
  };

  /**
   * Update remarks for a specific student
   */
  const handleRemarksChange = (studentId, text) => {
    setRemarksMap((prev) => ({
      ...prev,
      [studentId]: text
    }));
  };

  /**
   * Quick action to set all students to Present
   */
  const handleMarkAllPresent = () => {
    const updated = {};
    students.forEach((st) => {
      updated[st.student_id] = 'present';
    });
    setAttendanceMap(updated);
  };

  /**
   * Submit bulk attendance to backend API
   */
  const handleSubmitAttendance = async (e) => {
    e.preventDefault();
    if (students.length === 0) {
      setError('No students loaded to submit attendance');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      const recordsPayload = students.map((st) => ({
        student_id: st.student_id,
        section: st.section || selectedSection,
        status: attendanceMap[st.student_id] || 'present',
        remarks: remarksMap[st.student_id] || ''
      }));

      const response = await api.post('/api/v1/attendance', {
        class_id: selectedClass,
        section: selectedSection,
        date: selectedDate,
        records: recordsPayload
      });

      if (response.data?.success) {
        setSuccessMessage(`Attendance saved successfully for Class ${selectedClass} (${selectedDate})!`);
        setTimeout(() => setSuccessMessage(null), 4000);
      } else {
        setError(response.data?.message || 'Failed to save attendance');
      }
    } catch (err) {
      console.error('Error submitting attendance:', err);
      setError(err.response?.data?.message || err.message || 'Error saving attendance');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <PageHeader
          title="Mark Attendance"
          subtitle="Record daily student attendance by class and section"
          breadcrumbs={[
            { label: 'Attendance' },
            { label: 'Mark Attendance' }
          ]}
          backUrl="/dashboard/admin"
        >
          <Link
            to="/attendance/summary"
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-xs transition"
          >
            Summary Overview
          </Link>
          <Link
            to="/attendance/report"
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-xs transition"
          >
            Monthly Reports
          </Link>
        </PageHeader>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Controls Card */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            
            {/* Class Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Class <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="1">Class 1</option>
                <option value="2">Class 2</option>
                <option value="3">Class 3</option>
                <option value="4">Class 4</option>
                <option value="5">Class 5</option>
                <option value="6">Class 6</option>
                <option value="7">Class 7</option>
                <option value="8">Class 8</option>
                <option value="9">Class 9</option>
                <option value="10">Class 10</option>
                <option value="11">Class 11</option>
                <option value="12">Class 12</option>
              </select>
            </div>

            {/* Section Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Section
              </label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="A">Section A</option>
                <option value="B">Section B</option>
                <option value="C">Section C</option>
                <option value="D">Section D</option>
              </select>
            </div>

            {/* Date Selector */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Attendance Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Load Button */}
            <div>
              <button
                type="button"
                onClick={handleLoadStudents}
                disabled={loading}
                style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
                className="w-full py-2.5 text-white font-semibold rounded-xl text-sm shadow-sm transition hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? 'Loading...' : 'Load Students'}
              </button>
            </div>

          </div>
        </div>

        {/* Status Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800">✕</button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-sm font-semibold flex items-center justify-between shadow-sm">
            <span>{successMessage}</span>
          </div>
        )}

        {/* Student Roster Table */}
        <form onSubmit={handleSubmitAttendance} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-bold text-slate-800">
              Class {selectedClass} ({selectedSection}) • <span className="text-primary font-mono">{selectedDate}</span>
              <span className="ml-3 text-xs font-normal text-slate-500">({students.length} students)</span>
            </div>
            <button
              type="button"
              onClick={handleMarkAllPresent}
              className="px-3.5 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold rounded-xl text-xs transition"
            >
              ✓ Mark All Present
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3.5">Roll No</th>
                  <th className="px-6 py-3.5">Student Name</th>
                  <th className="px-6 py-3.5">Father Name</th>
                  <th className="px-6 py-3.5 text-center">Status</th>
                  <th className="px-6 py-3.5">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading class roster...</span>
                      </div>
                    </td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No active students found in Class {selectedClass} ({selectedSection}).
                    </td>
                  </tr>
                ) : (
                  students.map((student) => {
                    const currentStatus = attendanceMap[student.student_id] || 'present';
                    return (
                      <tr key={student.student_id} className="hover:bg-slate-50/80 transition">
                        <td className="px-6 py-4 font-mono font-bold text-slate-800 text-xs">
                          {student.roll_no || 'N/A'}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-900">
                          {student.student_name}
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {student.father_name}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-3">
                            
                            {/* Present Radio */}
                            <label className={`cursor-pointer px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${currentStatus === 'present' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                              <input
                                type="radio"
                                name={`status_${student.student_id}`}
                                value="present"
                                checked={currentStatus === 'present'}
                                onChange={() => handleStatusChange(student.student_id, 'present')}
                                className="hidden"
                              />
                              Present
                            </label>

                            {/* Absent Radio */}
                            <label className={`cursor-pointer px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${currentStatus === 'absent' ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                              <input
                                type="radio"
                                name={`status_${student.student_id}`}
                                value="absent"
                                checked={currentStatus === 'absent'}
                                onChange={() => handleStatusChange(student.student_id, 'absent')}
                                className="hidden"
                              />
                              Absent
                            </label>

                            {/* Late Radio */}
                            <label className={`cursor-pointer px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${currentStatus === 'late' ? 'bg-amber-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                              <input
                                type="radio"
                                name={`status_${student.student_id}`}
                                value="late"
                                checked={currentStatus === 'late'}
                                onChange={() => handleStatusChange(student.student_id, 'late')}
                                className="hidden"
                              />
                              Late
                            </label>

                            {/* Leave Radio */}
                            <label className={`cursor-pointer px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${currentStatus === 'leave' ? 'bg-blue-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                              <input
                                type="radio"
                                name={`status_${student.student_id}`}
                                value="leave"
                                checked={currentStatus === 'leave'}
                                onChange={() => handleStatusChange(student.student_id, 'leave')}
                                className="hidden"
                              />
                              Leave
                            </label>

                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            placeholder="Optional note..."
                            value={remarksMap[student.student_id] || ''}
                            onChange={(e) => handleRemarksChange(student.student_id, e.target.value)}
                            className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Form Actions */}
          {students.length > 0 && (
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
                className="px-8 py-3 text-white font-bold rounded-xl text-sm shadow-md transition hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && (
                  <svg className="w-4 h-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Submit Attendance
              </button>
            </div>
          )}
        </form>

      </main>
    </div>
  );
};

export default MarkAttendance;
